import type { Draft } from './DestinationFields';
import type {
  PaigeContentCentralType,
  PaigeDestination,
  PaigeDestinationInput,
  PaigeDestinationKind,
  PaigeFilingSettings,
  PaigeStorageKind,
} from './paige.global';

/** What the sheet and the card say about each kind a customer can add. */
export interface KindMeta {
  /** The noun in "Add a ..." and the add button ("Add webhook"). */
  noun: string;
  button: string;
  /** One line under the sheet's title saying what Paige sends there. */
  line: string;
  /** The kind in the card's list of what can be added ("add a webhook, an SFTP folder or Box"). */
  listed: string;
}

export const kindMeta: Record<PaigeDestinationKind, KindMeta> = {
  webhook: {
    noun: 'webhook',
    button: 'Add webhook',
    line: 'Paige posts each confirmed document as JSON and signs it with your secret (Paige-Signature). Retries on any answer that is not 2xx.',
    listed: 'a webhook',
  },
  sftp: {
    noun: 'SFTP folder',
    button: 'Add SFTP',
    line: "Paige puts each confirmed document in the folder as a PDF with its values inside, in the folders and under the name the job's file naming gives it (Job setup, Export).",
    listed: 'an SFTP folder',
  },
  'content-central': {
    noun: 'Content Central destination',
    button: 'Add Content Central',
    line: 'Paige files each confirmed document as a PDF in the catalog under the document type, with its fields as index values. The integration key comes from Content Central.',
    listed: 'Content Central',
  },
  'nucleus-one': {
    noun: 'Nucleus One destination',
    button: 'Add Nucleus One',
    line: "Paige files each confirmed document as a PDF in the project, in the folders and under the name the job's file naming gives it, with its fields.",
    listed: 'Nucleus One',
  },
  'microsoft-365': {
    noun: 'Microsoft 365 destination',
    button: 'Add Microsoft 365 (SharePoint / OneDrive)',
    line: "Paige files each confirmed document as a PDF in a SharePoint library or OneDrive folder, in the folders and under the name the job's file naming gives it.",
    listed: 'Microsoft 365',
  },
  'google-drive': {
    noun: 'Google Drive destination',
    button: 'Add Google Drive',
    line: "Paige files each confirmed document as a PDF in a Google Drive folder or shared drive, in the folders and under the name the job's file naming gives it.",
    listed: 'Google Drive',
  },
  dropbox: {
    noun: 'Dropbox destination',
    button: 'Add Dropbox',
    line: "Paige files each confirmed document as a PDF in a Dropbox folder, team folders included, in the folders and under the name the job's file naming gives it.",
    listed: 'Dropbox',
  },
  box: {
    noun: 'Box destination',
    button: 'Add Box',
    line: "Paige files each confirmed document as a PDF in a Box folder, in the folders and under the name the job's file naming gives it, with its values as Box metadata.",
    listed: 'Box',
  },
};

/** How a product names the places a field's value is written (a library's columns, Box metadata). */
export interface MetadataLabels {
  targetLabel: string;
  legend: string;
  hint: string;
  /** Whether a field not listed is written too, under its own name (Box), so an empty target means "leave out". */
  allByDefault?: boolean;
  /**
   * The choice to write every field under its own name as well (FilingSettings.sameNames), in CapturePoint's words
   * (shared/cloud-export.ts, CLOUD_PROVIDER_WORDS.values); absent where every field goes already (Box).
   */
  sameNames?: string;
}

export const columnMetadata: MetadataLabels = {
  targetLabel: 'Column',
  legend: 'Columns',
  hint: 'A field listed here goes to the column you name. Every other field fills the column of its own name when the box above is ticked and the library has one.',
  sameNames: 'Also fill SharePoint columns with the same names',
};

/** Box files every field as a custom property of its own name unless a line says otherwise (BoxStorage). */
export const boxMetadata: MetadataLabels = {
  targetLabel: 'Box metadata',
  legend: 'Metadata',
  hint: "Every field goes into the file's Box properties under its own name. Add a line to send a field elsewhere: Template/Field fills a field of one of your company's metadata templates (for example Invoice Data/Vendor), another name renames the property, and an empty target leaves the field out.",
  allByDefault: true,
};

/** Google Drive writes each listed field as an app property and a line of the file's description (DriveSession.Properties). */
export const driveMetadata: MetadataLabels = {
  targetLabel: 'Property',
  legend: 'Properties',
  hint: "A field listed here is written to the file in Google Drive as a property of the name you give and as a line of the file's description (Vendor: Acme), which Drive shows in the file's details and searches. With the box above ticked, every other field is written under its own name too.",
  sameNames: "Also add the values to each file's Drive details",
};

/** A storage kind as its form and messages name it (the API's StorageProvider.product). */
export interface StorageProduct {
  product: string;
  /** Where field values go: a library's columns when not given; null where the product has none (Dropbox: the PDF carries them). */
  metadata?: MetadataLabels | null;
  /** The picker's search at the top, as the provider's search finds things; null where it has none. */
  search: string | null;
}

/** The storage kinds (ADR-0016): connected with the provider's sign-in, filed by the job's file naming. */
export const storageProducts: Record<PaigeStorageKind, StorageProduct> = {
  'microsoft-365': { product: 'Microsoft 365', search: 'Find a SharePoint site' },
  'google-drive': {
    product: 'Google Drive',
    metadata: driveMetadata,
    search: 'Search Google Drive folders and shared drives',
  },
  dropbox: { product: 'Dropbox', metadata: null, search: 'Search Dropbox folders' },
  box: { product: 'Box', metadata: boxMetadata, search: 'Search Box folders' },
};

export const storageKinds = Object.keys(storageProducts) as readonly PaigeStorageKind[];

export function isStorageKind(kind: PaigeDestination['kind']): kind is PaigeStorageKind {
  return (storageKinds as readonly string[]).includes(kind);
}

/** Content Central or Nucleus One: added with a key, then placed through Paige's pickers. */
export function isProductKind(
  kind: PaigeDestination['kind'],
): kind is 'content-central' | 'nucleus-one' {
  return kind === 'content-central' || kind === 'nucleus-one';
}

export const addableKinds = Object.keys(kindMeta) as PaigeDestinationKind[];

/** "a webhook, an SFTP folder, Dropbox or Box": the kinds offered here, as the card lists them. */
export function offeredList(kinds: readonly PaigeDestinationKind[]): string {
  const names = kinds.map((kind) => kindMeta[kind].listed);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} or ${names.at(-1)}`;
}

export function isAddable(kind: PaigeDestination['kind']): kind is PaigeDestinationKind {
  return kind in kindMeta;
}

/** The form's starting values: the stored settings, never a secret. */
export function draftOf(existing?: PaigeDestination): Draft {
  const s = existing?.settings ?? {};
  const text = (value: string | undefined) => value ?? '';
  return {
    label: text(existing?.label),
    url: text(s.url),
    secret: '',
    host: text(s.host),
    port: String(s.port ?? 22),
    username: text(s.username),
    folder: text(s.folder),
    password: '',
    privateKey: '',
    siteUrl: text(s.siteUrl),
    catalog: text(s.catalog),
    apiKey: '',
    organization: text(s.organization),
    project: text(s.project),
    classification: text(s.classification),
    types: typesOf(s),
    ...storageDraftOf(s, existing === undefined),
  };
}

/**
 * A Content Central destination's type map; one saved before the map (one document type and one
 * field map) reads as a line for the Paige type of that name, which is what the API makes of it.
 */
function typesOf(s: PaigeDestination['settings']): PaigeContentCentralType[] {
  if (s.types) return s.types.map((t) => ({ ...t, fieldMap: t.fieldMap ?? [] }));
  if (!s.documentType) return [];
  return [{ paigeType: s.documentType, documentType: s.documentType, fieldMap: s.fieldMap ?? [] }];
}

/** The type map as saved: field map lines without a Paige field left out. */
function typeMapOf(d: Draft): PaigeContentCentralType[] {
  return d.types.map((t) => ({
    paigeType: t.paigeType,
    documentType: t.documentType,
    fieldMap: (t.fieldMap ?? []).filter((line) => line.field.trim() !== ''),
  }));
}

/**
 * A storage destination's part of the draft: its place, and the fields it writes as the provider's
 * metadata. Folders and file names are the job's file naming, not the destination's.
 */
function storageDraftOf(s: PaigeDestination['settings'], isNew: boolean) {
  const location = s.location ?? { id: '', path: '' };
  return {
    locationId: location.id,
    locationPath: location.path,
    dataFile: dataFileOf(s.filing, isNew),
    sameNames: sameNamesOf(s.filing, isNew),
    fieldMap: s.fieldMap ?? s.filing?.metadata ?? [],
  };
}

/**
 * On for a new destination (parity E-07: the welcome and tip emails promise "a data file with every value, ready
 * for your next system", and CapturePoint's export to a folder writes the pair); a saved one shows what it files.
 */
function dataFileOf(filing: PaigeFilingSettings | undefined, isNew: boolean): string {
  const on = isNew ? filing?.dataFile !== false : filing?.dataFile === true;
  return on ? 'yes' : '';
}

/** On for a new destination, as in CapturePoint; one saved before the choice existed shows it off, as it files. */
function sameNamesOf(filing: PaigeFilingSettings | undefined, isNew: boolean): string {
  const on = isNew ? filing?.sameNames !== false : filing?.sameNames === true;
  return on ? 'yes' : '';
}

/**
 * The filing settings a storage draft stands for (the contract's FilingSettings): the metadata map.
 * Where every field is written unless a line says otherwise (Box), an empty target stays: it leaves
 * the field out. Elsewhere only listed fields are written, so an empty target is the field's own name.
 */
export function filingOf(d: Draft, kind?: PaigeStorageKind): PaigeFilingSettings {
  const metadata = kind ? storageProducts[kind].metadata : undefined;
  const allByDefault = metadata?.allByDefault === true;
  // Microsoft 365 when no labels are given (columnMetadata), Google Drive with its own; never Box or Dropbox.
  const offersSameNames =
    kind !== undefined && (metadata === undefined ? columnMetadata : metadata)?.sameNames;
  return {
    ...(offersSameNames ? { sameNames: d.sameNames === 'yes' } : {}),
    metadata: d.fieldMap
      .filter((line) => line.field.trim() !== '')
      .map((line) =>
        allByDefault || line.target.trim() !== '' ? line : { ...line, target: line.field },
      ),
    // Left out when off: a destination saved without it files the PDF alone.
    ...(d.dataFile === 'yes' ? { dataFile: true } : {}),
  };
}

/** The contract's DestinationInput; empty secrets are left out so an edit keeps the stored one. */
export function inputOf(kind: PaigeDestinationKind, d: Draft): PaigeDestinationInput {
  return { kind, label: d.label, settings: settingsOf(kind, d) };
}

function settingsOf(kind: PaigeDestinationKind, d: Draft): PaigeDestinationInput['settings'] {
  if (isStorageKind(kind)) return storageSettingsOf(kind, d);
  const fieldMap = d.fieldMap.filter((line) => line.field.trim() !== '');
  switch (kind) {
    case 'webhook':
      return { url: d.url, ...(d.secret ? { secret: d.secret } : {}) };
    case 'sftp':
      return {
        host: d.host,
        port: Number(d.port) || 22,
        username: d.username,
        folder: d.folder,
        ...sftpCredential(d),
      };
    case 'content-central':
      return {
        siteUrl: d.siteUrl,
        catalog: d.catalog,
        types: typeMapOf(d),
        ...(d.apiKey ? { apiKey: d.apiKey } : {}),
      };
    case 'nucleus-one':
      return nucleusOneSettingsOf(d, fieldMap);
  }
}

/** A Nucleus One destination's settings: the picked place names its ids; one saved before the picker keeps its own. */
function nucleusOneSettingsOf(
  d: Draft,
  fieldMap: Draft['fieldMap'],
): PaigeDestinationInput['settings'] {
  const place = d.locationId
    ? { location: { id: d.locationId, path: d.locationPath } }
    : { organization: d.organization, project: d.project, folder: d.folder };
  return {
    ...place,
    classification: d.classification,
    fieldMap,
    ...(d.apiKey ? { apiKey: d.apiKey } : {}),
  };
}

/** A storage destination's settings: the place once chosen, and the metadata map. */
function storageSettingsOf(kind: PaigeStorageKind, d: Draft): PaigeDestinationInput['settings'] {
  const location = d.locationId ? { location: { id: d.locationId, path: d.locationPath } } : {};
  return { ...location, filing: filingOf(d, kind) };
}

function sftpCredential(d: Draft): PaigeDestinationInput['settings'] {
  if (d.privateKey.trim()) return { privateKey: d.privateKey };
  return d.password ? { password: d.password } : {};
}
